import React, { useState } from 'react';
    import { NavLink, useNavigate } from 'react-router-dom';
    import { motion, AnimatePresence } from 'framer-motion';
    import { useAuth } from '../contexts/AuthContext';
    import SafeIcon from '../common/SafeIcon';
    import * as FiIcons from 'react-icons/fi';
    import { Triangle, Circle, Square, Hexagon } from './GeometricShapes';

    const {
      FiUsers,
      FiBookOpen,
      FiMessageCircle,
      FiShoppingBag,
      FiMenu,
      FiHome,
      FiTarget,
      FiEye,
      FiInfo,
      FiSettings,
      FiHeart,
      FiUser,
      FiStar,
      FiAward,
      FiLogOut,
      FiBell,
      FiShield,
    } = FiIcons;

    const MobileNavbar = () => {
      const { user, logout } = useAuth();
      const navigate = useNavigate();
      const [isPressed, setIsPressed] = useState(false);
      const [showMenu, setShowMenu] = useState(false);

      // Main navigation categories
      const menuCategories = [
        {
          title: 'Main Navigation',
          items: [
            { to: '/', icon: FiHome, label: 'Home', description: 'Return to dashboard' },
            { to: '/study-room', icon: FiBookOpen, label: 'Study Room', description: 'Access quizzes and materials' },
            { to: '/community', icon: FiUsers, label: 'Community', description: 'Connect with learners' },
            { to: '/chat', icon: FiMessageCircle, label: 'Chat', description: 'Message other students' },
            { to: '/marketplace', icon: FiShoppingBag, label: 'Marketplace', description: 'Buy and sell materials' },
          ],
        },
        {
          title: 'Organization',
          items: [
            { to: '/mission', icon: FiTarget, label: 'Our Mission', description: 'Learn about our goals' },
            { to: '/vision', icon: FiEye, label: 'Our Vision', description: 'See our future plans' },
            { to: '/about', icon: FiInfo, label: 'About Us', description: 'Who we are' },
            { to: '/organization', icon: FiHeart, label: 'Who We Are', description: 'Our team and values' },
          ],
        },
        {
          title: 'Personal',
          items: [
            { to: '/profile', icon: FiUser, label: 'Profile', description: 'View and edit profile' },
            { to: '/study-groups', icon: FiUsers, label: 'Study Groups', description: 'Join study groups' },
            { to: '/feedback', icon: FiStar, label: 'Feedback', description: 'Share your thoughts' },
            { to: '/settings', icon: FiSettings, label: 'Settings', description: 'Customize experience' },
          ],
        },
      ];

      // Always add admin section
      menuCategories.push({
        title: 'Administration',
        items: [{ to: '/admin', icon: FiShield, label: 'Admin Panel', description: 'Manage app settings' }],
      });

      const bottomNavItems = [
        { to: '/community', icon: FiUsers, label: 'Community' },
        { to: '/marketplace', icon: FiShoppingBag, label: 'Shop' },
        { to: '/', icon: FiMenu, label: null }, // Menu button
        { to: '/chat', icon: FiMessageCircle, label: 'Chat' },
        { to: '/study-room', icon: FiBookOpen, label: 'Study' },
      ];

      const handleNavItemClick = () => {
        if (navigator.vibrate) {
          navigator.vibrate(10);
        }
      };

      const handleMenuPress = () => {
        setIsPressed(true);
        if (navigator.vibrate) {
          navigator.vibrate(15);
        }
        setShowMenu(!showMenu);
        setTimeout(() => setIsPressed(false), 150);
      };

      const handleLogout = () => {
        logout();
        setShowMenu(false);
        navigate('/login');
      };

      return (
        <>
          {/* Main Navigation Bar */}
          <nav className="mobile-nav-bar bg-white/95 dark:bg-neutral-900/95 backdrop-blur-md border-t border-neutral-200 dark:border-neutral-800">
            <div className="flex justify-around items-center h-16 relative">
              {bottomNavItems.map((item, index) => (
                <NavLink
                  key={index}
                  to={item.to}
                  onClick={item.label ? handleNavItemClick : handleMenuPress}
                  className={({ isActive }) =>
                    `flex flex-col items-center justify-center py-1 px-1 touch-feedback ${
                      isActive ? 'text-primary-600 dark:text-primary-400' : 'text-neutral-500 dark:text-neutral-400'
                    } ${!item.label ? 'relative' : ''}`
                  }
                  aria-label={item.label || 'Menu'}
                >
                  {!item.label ? (
                    // Geometric menu icon design
                    <motion.div
                      className="relative"
                      initial="initial"
                      animate={{ rotate: showMenu ? 45 : 0, scale: isPressed ? 0.9 : 1 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl shadow-geometric-lg flex items-center justify-center overflow-hidden transform transition-all duration-300 hover:scale-105 relative">
                        {/* Geometric background pattern */}
                        <div className="absolute inset-0 opacity-20">
                          <div className="absolute top-1 left-1">
                            <Triangle size="sm" color="primary" className="text-white" />
                          </div>
                          <div className="absolute bottom-1 right-1">
                            <Circle size="sm" color="primary" className="text-white" />
                          </div>
                        </div>
                        {/* Logo Text */}
                        <div className="relative z-10 flex items-center justify-center">
                          <span className="text-white font-bold text-lg drop-shadow-lg">ILM</span>
                        </div>
                        {/* Geometric border */}
                        <div className="absolute inset-0 rounded-2xl border-2 border-white/20"></div>
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div whileHover={{ y: -4 }} className="flex flex-col items-center text-center">
                      <SafeIcon icon={item.icon} className="w-4 h-4 mb-1" />
                      <span className="text-[9px] leading-tight nav-text max-w-[60px]">{item.label}</span>
                    </motion.div>
                  )}
                </NavLink>
              ))}
            </div>
          </nav>

          {/* Full Screen Menu Overlay */}
          <AnimatePresence>
            {showMenu && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-neutral-900/50 z-40 backdrop-blur-sm"
                onClick={() => setShowMenu(false)}
              >
                <motion.div
                  initial={{ y: '100%' }}
                  animate={{ y: 0 }}
                  exit={{ y: '100%' }}
                  transition={{ type: 'spring', damping: 25 }}
                  className="absolute bottom-0 left-0 right-0 max-h-[80vh] bg-white dark:bg-neutral-800 rounded-t-3xl overflow-y-auto shadow-geometric-xl border-t-2 border-neutral-200 dark:border-neutral-700"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Geometric handle */}
                  <div className="flex justify-center pt-3 pb-2">
                    <div className="w-12 h-1 bg-neutral-300 dark:bg-neutral-600 rounded-full"></div>
                  </div>
                  <div className="p-4">
                    {/* User Profile Summary with Geometric Design */}
                    <div className="flex items-center space-x-3 mb-6 pb-4 border-b border-neutral-200 dark:border-neutral-700 relative">
                      {/* Geometric decorations */}
                      <div className="absolute top-0 right-4">
                        <Triangle size="sm" color="blue" rotation={15} className="opacity-20" />
                      </div>
                      <div className="relative flex-shrink-0">
                        <img
                          src={user?.avatar}
                          alt={user?.name}
                          className="w-14 h-14 rounded-2xl object-cover border-2 border-primary-100 dark:border-primary-900 shadow-geometric"
                        />
                        <div className="absolute -bottom-1 -right-1">
                          <Circle size="sm" color="green" filled className="text-green-500" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-neutral-800 dark:text-neutral-200 text-ellipsis">{user?.name}</h3>
                        <p className="text-sm text-neutral-500 dark:text-neutral-400 text-ellipsis">{user?.email}</p>
                        <span className="inline-block mt-1 bg-red-100 text-red-700 px-2 py-1 rounded-lg text-xs font-medium">
                          Admin
                        </span>
                      </div>
                      <div className="flex flex-col items-end flex-shrink-0">
                        <div className="bg-primary-100 dark:bg-primary-900/50 text-primary-600 dark:text-primary-300 px-2 py-1 rounded-xl text-xs font-medium border border-primary-200 dark:border-primary-800">
                          {user?.points} pts
                        </div>
                        <span className="text-xs text-neutral-500 dark:text-neutral-400 mt-1 text-ellipsis">{user?.level}</span>
                      </div>
                    </div>

                    {/* Menu Categories with Geometric Design */}
                    <div className="space-y-4 pb-20">
                      {menuCategories.map((category, categoryIndex) => (
                        <div key={categoryIndex} className="space-y-3">
                          <h3 className="text-sm font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider flex items-center">
                            <div className="w-2 h-2 bg-primary-500 rounded-sm mr-2 flex-shrink-0"></div>
                            <span className="text-ellipsis">{category.title}</span>
                          </h3>
                          <div className="grid grid-cols-2 gap-2">
                            {category.items.map((item, itemIndex) => (
                              <motion.button
                                key={itemIndex}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                className="flex items-center space-x-2 p-3 rounded-xl bg-gradient-to-br from-neutral-50 to-white dark:from-neutral-700 dark:to-neutral-800 hover:from-neutral-100 hover:to-neutral-50 dark:hover:from-neutral-600 dark:hover:to-neutral-700 transition-all text-left border-2 border-neutral-100 dark:border-neutral-600 hover:border-primary-200 dark:hover:border-primary-600 shadow-geometric hover:shadow-geometric-lg group relative overflow-hidden"
                                onClick={() => {
                                  setShowMenu(false);
                                  navigate(item.to);
                                }}
                              >
                                {/* Geometric decoration */}
                                <div className="absolute top-1 right-1 opacity-5 group-hover:opacity-10 transition-opacity">
                                  <Hexagon size="md" color="primary" />
                                </div>
                                <div className="flex-shrink-0 w-8 h-8 rounded-xl bg-white dark:bg-neutral-600 flex items-center justify-center shadow-geometric border border-neutral-200 dark:border-neutral-500 group-hover:border-primary-300 dark:group-hover:border-primary-500 transition-colors relative z-10">
                                  <SafeIcon icon={item.icon} className="w-4 h-4 text-primary-600 dark:text-primary-300" />
                                </div>
                                <div className="flex-1 min-w-0 relative z-10">
                                  <div className="font-medium text-neutral-800 dark:text-neutral-200 text-sm group-hover:text-neutral-900 dark:group-hover:text-white text-ellipsis">
                                    {item.label}
                                  </div>
                                  <div className="text-neutral-500 dark:text-neutral-400 text-xs line-clamp-1">{item.description}</div>
                                </div>
                              </motion.button>
                            ))}
                          </div>
                        </div>
                      ))}
                      {/* Logout Button with Geometric Design */}
                      <div className="mt-6 sticky bottom-0 pb-4 pt-2 bg-white dark:bg-neutral-800">
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={handleLogout}
                          className="w-full flex items-center justify-center space-x-2 p-3 rounded-2xl bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/50 dark:to-red-900/30 text-red-600 dark:text-red-300 font-medium hover:from-red-100 hover:to-red-200 dark:hover:from-red-900/70 dark:hover:to-red-900/50 transition-all border-2 border-red-200 dark:border-red-800 hover:border-red-300 dark:hover:border-red-700 shadow-geometric"
                        >
                          <SafeIcon icon={FiLogOut} className="w-4 h-4" />
                          <span>Logout</span>
                        </motion.button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      );
    };

    export default MobileNavbar;